import { Alert, Button, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { DollarSign, AlertTriangle } from 'react-feather';

interface CompensationCalloutsProps {
  paymentsPendingCount?: number;
  paymentsOverdueCount?: number;
  unlinkedCoachCount?: number;
  onReviewPayments?: () => void;
}

/**
 * Callout banners at top of Compensation tab (AC0a, AC0b).
 *
 * E13-4 stub: Both banners are hidden when their counts are 0.
 * Real data will flow from E13-5 (Bill.com integration) and E13-6 (Net-10 payments).
 */
export function CompensationCallouts({
  paymentsPendingCount = 0,
  paymentsOverdueCount = 0,
  unlinkedCoachCount = 0,
  onReviewPayments,
}: CompensationCalloutsProps) {
  const totalPaymentsAwaiting = paymentsPendingCount + paymentsOverdueCount;

  if (totalPaymentsAwaiting === 0 && unlinkedCoachCount === 0) {
    return null;
  }

  return (
    <Stack gap="sm">
      {totalPaymentsAwaiting > 0 && (
        <Alert
          color="medleyGreen"
          variant="light"
          styles={{ root: { borderRadius: 8 } }}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon color="medleyGreen" variant="light" radius="xl" size={36}>
                <DollarSign size={18} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="sm">
                  {totalPaymentsAwaiting} {totalPaymentsAwaiting === 1 ? 'payment' : 'payments'}{' '}
                  awaiting submission to Bill.com
                </Text>
                <Text size="xs" c="dimmed">
                  {paymentsOverdueCount > 0 && (
                    <Text component="span" c="dangerRed" fw={600}>
                      {paymentsOverdueCount} overdue
                    </Text>
                  )}
                  {paymentsOverdueCount > 0 && paymentsPendingCount > 0 && ' · '}
                  {paymentsPendingCount > 0 && `${paymentsPendingCount} pending`}
                </Text>
              </div>
            </Group>
            {onReviewPayments && (
              <Button onClick={onReviewPayments} size="sm">
                Review Payments
              </Button>
            )}
          </Group>
        </Alert>
      )}

      {unlinkedCoachCount > 0 && (
        <Alert
          color="warningAmber"
          variant="light"
          icon={<AlertTriangle size={18} />}
          styles={{ root: { borderRadius: 8 } }}
        >
          <Text size="sm">
            <Text component="span" fw={600}>
              {unlinkedCoachCount} {unlinkedCoachCount === 1 ? 'coach' : 'coaches'} not linked to
              Bill.com
            </Text>{' '}
            — payments cannot be scheduled until all coaches are mapped to a Bill.com vendor
          </Text>
        </Alert>
      )}
    </Stack>
  );
}
